// 時間前入札の承認モードを、 承認フロー設定 (`ApprovalFlowService.GetApprovalFlowScreen`)
// から解決する (AC-BIN-009 / #6650)。
//
// 時間前の札作成は 1 回の Create RPC が常に単一エリアを対象にする (個別作成 modal も
// 一括作成 modal もフォームで選んだ 1 エリアぶんをループ生成するだけで、 spot の
// 「1 申請セットが複数エリアの札を束ねる」 ケースが無い)。 そのため spot
// (`SpotBidding/models/approvalModeResolve.ts`) の「対象エリア群が承認なしで揃うか」
// という uniform 判定は不要で、 対象 1 エリアのセルをそのまま引けばよい。
//
// 承認ウィザードの手順表示 (旧 AC-BIN-094) は 2026-10-05 の PdM 判断で廃止した (#8312。
// 札ごとの進み具合はステータス列、 承認の状態は承認状態バナーで読める)。 そのため
// page-level エリアフィルタから承認モードを解決する関数は持たない。
// 設定を解決できない間 (取得中・取得失敗・オフラインで `data === undefined` のまま) は、
// この解決自体を書込に使わせない (= 作成・一括作成ボタンを非活性にし、 確定処理でも
// 弾いて、 フォールバックで作成する経路を無くす)。 判定述語は本 module の
// `isIntradayApprovalFlowUnresolved` 1 本に集約し、 `BidCreateTab.tsx` の
// `isApprovalFlowUnavailable` と `useIntradayBidCreateSubmit.ts` の両方がこれを使う。
import {
  ApprovalMode as ApprovalFlowCellMode,
  BusinessProcess,
} from '@ebalance/proto/bfftypes/v1/approval_flow_type_pb'
import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import type {
  ApprovalFlowCell,
  ApprovalFlowProcessSetting,
} from '@ebalance/proto/bffuser/v1/approval_flow_model_pb'
import { BiddingApprovalMode } from '@ebalance/proto/bffuser/v1/bidding_common_model_pb'

/** これまでの固定挙動 (暫定配線 `AUTO`) と同じ、 未解決時のフォールバック値。 */
export const INTRADAY_APPROVAL_MODE_FALLBACK = BiddingApprovalMode.AUTO

/**
 * 承認フロー設定のセル値 (`bfftypes.v1.ApprovalMode`) を bidding の承認モード
 * (`bffuser.v1.BiddingApprovalMode`) に変換する。
 *
 * 数値は現状揃っている (UNSPECIFIED=0 / APPROVAL=1 / AUTO=2 / NO_APPROVAL=3) が、
 * 由来の異なる enum を暗黙の数値キャストで結び付けず、 変換点をここ 1 か所に閉じる
 * (どちらかの proto が値を追加・並べ替えても破綻しないようにする。
 * spot 側 `approvalModeResolve.ts` の同名関数と同じ理由で意図的に重複させている
 * — 画面をまたいだ import は避ける方針、 write seam パターンと同じ)。
 */
function toBiddingApprovalMode(
  mode: ApprovalFlowCellMode,
): BiddingApprovalMode {
  switch (mode) {
    case ApprovalFlowCellMode.APPROVAL:
      return BiddingApprovalMode.APPROVAL
    case ApprovalFlowCellMode.AUTO:
      return BiddingApprovalMode.AUTO
    case ApprovalFlowCellMode.NO_APPROVAL:
      return BiddingApprovalMode.NO_APPROVAL
    case ApprovalFlowCellMode.UNSPECIFIED:
    default:
      return BiddingApprovalMode.UNSPECIFIED
  }
}

/**
 * 時間前入札 × 1 エリアの承認フロー設定セルを探す。
 *
 * 時間前入札は断面・スケジュール軸を持たない単一行マトリクス
 * (`docs/specs/admin/operation/approval/approval-flow.md` §1) なので、
 * `section` / `schedule` が未設定のセルだけを対象にする。
 */
export function findIntradayApprovalFlowCell(
  processes: readonly ApprovalFlowProcessSetting[],
  areaCode: AreaCode | undefined,
): ApprovalFlowCell | undefined {
  const setting = processes.find(
    (p) => p.businessProcess === BusinessProcess.INTRADAY_BID,
  )
  return setting?.cells.find(
    (c) =>
      c.areaCode === areaCode &&
      c.section === undefined &&
      c.schedule === undefined,
  )
}

/**
 * 1 エリアの承認モードを、 承認フロー設定 (`GetApprovalFlowScreen` の `processes`)
 * から解決する。
 *
 * 時間前入札は断面・スケジュール軸を持たない単一行マトリクス
 * (`docs/specs/admin/operation/approval/approval-flow.md` §1) なので、
 * `section` / `schedule` は常に未設定のセルを探す。 セルが無い (= 個別設定していない
 * エリア、 または新規テナントで未保存) 場合は spec §1 の新規テナント既定「承認なし」
 * を返す (`AdminApprovalFlow` の `convertProcessSetting` と同じ default)。
 *
 * セルはあるが値が `UNSPECIFIED` (= backend 側でも未設定。 BFF の read converter
 * `approval_flow_process_setting.go` は未知の enum 値もここへ落とす) の場合も同じく
 * 「承認なし」に畳む。 `AdminApprovalFlow` の `convertProcessSetting`
 * (`models/converter.ts`) が UNSPECIFIED セルを skip して承認なし既定を維持するのと
 * 同じ解釈に揃える (揃えないと `BiddingApprovalMode.UNSPECIFIED` をそのまま Create
 * request に載せてしまい、 backend の `approvalMode.Validate()` で汎用エラーになる)。
 */
function resolveAreaApprovalMode(
  processes: readonly ApprovalFlowProcessSetting[],
  areaCode: AreaCode,
): BiddingApprovalMode {
  const cell = findIntradayApprovalFlowCell(processes, areaCode)
  const mode =
    cell !== undefined
      ? toBiddingApprovalMode(cell.approvalMode)
      : BiddingApprovalMode.UNSPECIFIED
  return mode === BiddingApprovalMode.UNSPECIFIED
    ? BiddingApprovalMode.NO_APPROVAL
    : mode
}

/**
 * 承認フロー設定 (`GetApprovalFlowScreen`) がまだ解決できていない = 承認モードを
 * 確定できない状態か。 **作成 (個別作成・一括作成・板発注のクイック入札) の導線と
 * 確定処理は、 この述語が true の間ブロックする** (2026-09-16 決定: フォールバック
 * `AUTO` で要承認エリアの札を作成できる経路を残さない)。
 *
 * `isLoading` / `isError` だけでなく `processes === undefined` も見る:
 * react-query の既定 `networkMode: 'online'` ではオフライン中に fetchStatus が
 * `paused` になり、 `isLoading=false` / `isError=false` / `data=undefined` が
 * 成立しうる (`providers/ApiClientProvider.tsx` は networkMode を指定していない)。
 * この窓を塞がないと `resolveIntradayApprovalMode` のフォールバック条件
 * (`processes === undefined` を含む) と gate 条件がズレる。
 */
export function isIntradayApprovalFlowUnresolved(params: {
  processes: readonly ApprovalFlowProcessSetting[] | undefined
  isLoading: boolean
  isError: boolean
}): boolean {
  const { processes, isLoading, isError } = params
  return isLoading || isError || processes === undefined
}

/**
 * 時間前入札の承認モードを解決する (AC-BIN-009 / #6650)。
 *
 * - `areaCode` が確定していない (= 未指定。 page-level エリアフィルタが複数選択・
 *   「すべて」 で単一エリアに絞れない場合はこの関数を呼ぶ側で `undefined` を渡す) 間は
 *   フォールバック値を返す。
 * - `GetApprovalFlowScreen` が未取得・取得失敗の間もフォールバック値を返すが、
 *   **書込 (Create) 経路ではこの分岐に入ってはいけない**。 2026-09-16 決定
 *   「フォールバックで作成できる経路を無くす」 に従い、 書込の呼び出し側
 *   (`BidCreateTab` の個別作成・一括作成、 `useIntradayBidCreateSubmit`) は
 *   `isIntradayApprovalFlowUnresolved` で先に弾いてから本関数を呼ぶ契約になっている
 *   (= 到達時点で `isLoading` / `isError` / `processes === undefined` は必ず false)。
 * - 上記いずれでもなければ、 対象エリアの承認フロー設定 (要承認 / 自動 / 承認なし) を
 *   そのまま返す。
 */
export function resolveIntradayApprovalMode(params: {
  processes: readonly ApprovalFlowProcessSetting[] | undefined
  isLoading: boolean
  isError: boolean
  areaCode: AreaCode | undefined
}): BiddingApprovalMode {
  const { processes, isLoading, isError, areaCode } = params
  if (
    areaCode === undefined ||
    isLoading ||
    isError ||
    processes === undefined
  ) {
    return INTRADAY_APPROVAL_MODE_FALLBACK
  }
  return resolveAreaApprovalMode(processes, areaCode)
}
